/**
 * Avant l'audience : le dossier se lit en entier à gauche, la composition de
 * la cour est à droite — chaque siège libre a son propre bouton.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getDateLocale } from '@/i18n';

import { CaseDossier, dossierTabs, type DossierTab } from './CaseDossier';
import { CourtDiorama, TrialAvatar } from './Courtroom';
import type { Side, Trial } from './trialTypes';

const FLOW = ['opening', 'examination', 'evidence', 'closing', 'verdict'] as const;

export function WaitingRoom({
  trial, myRole, canJoin, onJoin, onInvite,
}: {
  trial: Trial;
  myRole: Side | null;
  canJoin: boolean;
  onJoin: (side: Side) => void;
  onInvite: () => void;
}) {
  const { t } = useTranslation();
  const gc = trial.generated_case;
  const famous = gc.famous;
  const [tab, setTab] = useState<DossierTab>('facts');
  const tabs = dossierTabs(trial, myRole);

  const seat = (side: Side) => {
    const taken = side === 'prosecution' ? trial.prosecution_taken : trial.defense_taken;
    const name  = side === 'prosecution' ? trial.prosecution_name : trial.defense_name;
    const mine  = myRole === side;
    const isP   = side === 'prosecution';
    const tone  = isP ? 'text-[#9b3a2e] dark:text-[#c47a68]' : 'text-[#3d5a73] dark:text-[#8aa3b8]';
    if (taken) {
      return (
        <div className="p-4 rounded-2xl border border-border bg-card flex items-center gap-3.5">
          <TrialAvatar role={side} size={52} />
          <div className="flex-1 min-w-0">
            <div className={`text-[11px] font-bold uppercase tracking-wider ${tone}`}>
              {t(`tribunal.seats.${side}`)}{mine && <span className="ml-1 normal-case tracking-normal text-muted-foreground">{t('tribunal.participant.you')}</span>}
            </div>
            <div className="font-bold text-foreground truncate">{name}</div>
            <div className="text-xs text-muted-foreground">{t('tribunal.court.seated')}</div>
          </div>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[#4f7a6a] shrink-0" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
        </div>
      );
    }
    return (
      <div className={`p-4 rounded-2xl border-2 border-dashed flex flex-col gap-3.5 ${isP ? 'border-[#d4a090] bg-[#f6e9e3]/60 dark:border-[#7e2c22] dark:bg-[#3a150f]/20' : 'border-[#9fb2c1] bg-[#e9eef1]/60 dark:border-[#2f4659] dark:bg-[#18242d]/20'}`}>
        <div className="flex items-center gap-3.5">
          <span aria-hidden="true" className={`w-[52px] h-[52px] shrink-0 rounded-full border-2 border-dashed flex items-center justify-center ${isP ? 'border-[#c47a68] text-[#9b3a2e]' : 'border-[#8aa3b8] text-[#3d5a73]'}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
          </span>
          <div>
            <div className={`text-[11px] font-bold uppercase tracking-wider ${tone}`}>{t(`tribunal.seats.${side}`)}</div>
            <div className="font-bold text-foreground">{t('tribunal.seats.free')}</div>
          </div>
        </div>
        {canJoin && (
          <button
            type="button"
            onClick={() => onJoin(side)}
            className={`h-12 rounded-xl text-white text-[15px] font-bold transition-colors ${isP ? 'bg-[#9b3a2e] hover:bg-[#7e2c22]' : 'bg-[#3d5a73] hover:bg-[#2f4659]'}`}
          >
            {t(isP ? 'tribunal.court.plead_prosecution' : 'tribunal.court.plead_defense')}
          </button>
        )}
        {myRole && !mine && (
          <button
            type="button"
            onClick={onInvite}
            className="h-11 rounded-xl border border-border bg-background text-sm font-semibold text-foreground hover:bg-muted"
          >
            {t('tribunal.court.invite_colleague')}
          </button>
        )}
      </div>
    );
  };

  // L'action du moment, visible sans défiler : prendre un siège, ou — une fois
  // assis — attendre l'adversaire et pouvoir l'inviter.
  const freeSides = (['prosecution', 'defense'] as const).filter(side =>
    !(side === 'prosecution' ? trial.prosecution_taken : trial.defense_taken));
  const waitingFor = myRole ? freeSides.find(side => side !== myRole) : undefined;
  const nextStep = (compact: boolean) => {
    if (canJoin && freeSides.length) {
      return (
        <div className="flex flex-col gap-2">
          {!compact && <p className="text-sm text-foreground/80">{t('tribunal.court.choose_side')}</p>}
          <div className="flex gap-2">
            {freeSides.map(side => (
              <button
                key={side}
                type="button"
                onClick={() => onJoin(side)}
                className={`flex-1 h-12 px-4 rounded-xl text-white text-[15px] font-bold transition-colors ${side === 'prosecution' ? 'bg-[#9b3a2e] hover:bg-[#7e2c22]' : 'bg-[#3d5a73] hover:bg-[#2f4659]'}`}
              >
                {t(side === 'prosecution' ? 'tribunal.court.plead_prosecution' : 'tribunal.court.plead_defense')}
              </button>
            ))}
          </div>
        </div>
      );
    }
    if (waitingFor) {
      return (
        <div className="flex items-center gap-3">
          <p role="status" className="flex-1 min-w-0 text-sm text-foreground/80 leading-snug">
            {t(`tribunal.court.waiting_for_${waitingFor}`)}
          </p>
          <button
            type="button"
            onClick={onInvite}
            className="shrink-0 h-12 px-4 rounded-xl bg-[#a8742a] hover:bg-[#8f6122] text-white text-[15px] font-bold transition-colors"
          >
            {t('tribunal.court.invite_short')}
          </button>
        </div>
      );
    }
    return null;
  };
  const bar = nextStep(true);

  const emptySeats = [
    ...(!trial.prosecution_taken ? ['prosecution' as const] : []),
    ...(!trial.defense_taken ? ['defense' as const] : []),
  ];
  const famousTag = famous && <span className="font-bold uppercase tracking-wider text-stone-900 bg-[#e0b866] px-2 py-1 rounded-md">{famous.label}</span>;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      {/* La salle en estampe : les sièges libres attendent leurs avocats */}
      <section className="relative h-56 sm:h-80 lg:h-[420px] overflow-hidden border-b border-[#8a7656]">
        <CourtDiorama emptySeats={emptySeats} showWitness={false} span={1250} />
        <div aria-hidden="true" className="hidden lg:block absolute inset-0 bg-gradient-to-r from-[#faf8f4] via-[#faf8f4]/90 via-35% to-transparent to-60%" />
        <div className="hidden lg:block absolute left-14 top-14 w-[560px]">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {famousTag}
            {famous?.date && (
              <span className="text-muted-foreground">
                {t('tribunal.court.real_verdict_on', { date: new Date(famous.date).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) })}
              </span>
            )}
          </div>
          <h1 className="mt-3 font-['IM_Fell_English',Georgia,serif] text-5xl leading-[1.02] text-foreground">{trial.title}</h1>
          {trial.headline && <p className="mt-4 text-[17px] leading-relaxed text-foreground/80">{trial.headline}</p>}
          {bar && <div className="mt-7 max-w-[480px]">{nextStep(false)}</div>}
        </div>
      </section>

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_440px]">
      {/* Le dossier */}
      <section className="px-4 sm:px-8 lg:pl-14 lg:pr-10 pt-6 lg:pt-8">
        <div className="lg:hidden">
          <div className="flex flex-wrap items-center gap-2 text-xs">{famousTag}</div>
          <h1 className="mt-2 font-['IM_Fell_English',Georgia,serif] text-3xl sm:text-4xl leading-tight text-foreground">{trial.title}</h1>
          {trial.headline && <p className="mt-3 text-base leading-relaxed text-foreground/80">{trial.headline}</p>}
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
          <div className="p-3.5 rounded-xl border border-border bg-card">
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{t('tribunal.room.defendant')}</div>
            <div className="mt-1 font-bold text-foreground">{gc.defendant.name}</div>
            <div className="text-xs text-muted-foreground">{t('tribunal.room.age_profession', { age: gc.defendant.age, profession: gc.defendant.profession })}</div>
          </div>
          <div className="p-3.5 rounded-xl border border-border bg-card">
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{t('tribunal.court.tabs.witnesses')}</div>
            <div className="mt-1 font-bold text-foreground">{t('tribunal.court.witnesses_n', { count: gc.witnesses?.length ?? 0 })}</div>
            <div className="text-xs text-muted-foreground truncate">{gc.witnesses?.map(w => w.name.split(' ').slice(-1)[0]).join(', ')}</div>
          </div>
          <div className="p-3.5 rounded-xl border border-border bg-card">
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{t('tribunal.court.tabs.evidence')}</div>
            <div className="mt-1 font-bold text-foreground">{t('tribunal.court.evidence_n', { count: gc.available_evidence?.length ?? 0 })}</div>
            <div className="text-xs text-muted-foreground truncate">{gc.available_evidence?.map(e => e.title).join(', ')}</div>
          </div>
        </div>

        <div className="mt-5 max-w-3xl rounded-2xl border border-border bg-card overflow-hidden flex flex-col min-h-[420px] mb-6">
          <CaseDossier
            trial={trial}
            myRole={myRole}
            tab={tabs.includes(tab) ? tab : tabs[0]}
            onTab={setTab}
            canQuestion={false}
            canFile={false}
          />
        </div>
      </section>

      {/* La composition de la cour */}
      <aside className="px-4 sm:px-8 py-6 lg:py-8 flex flex-col gap-4">
        <h2 className="font-['IM_Fell_English',Georgia,serif] text-2xl text-foreground">{t('tribunal.court.composition')}</h2>
        <div className="p-4 rounded-2xl border border-border bg-card flex items-center gap-3.5">
          <TrialAvatar role="judge" size={52} />
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#6e4a1a] dark:text-[#e0b866]">{t('tribunal.court.ai_court')}</div>
            <div className="font-bold text-foreground">{t('tribunal.roles.president')}</div>
            <div className="text-xs text-muted-foreground">{t('tribunal.court.ai_court_desc')}</div>
          </div>
        </div>
        {seat('prosecution')}
        {seat('defense')}

        <div className="mt-2 pt-5 border-t border-border">
          <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('tribunal.court.flow_title')}</h2>
          <ol className="flex flex-col gap-2.5 text-[13px]">
            {FLOW.map((p, i) => (
              <li key={p} className="flex gap-2.5">
                <span className="w-5 h-5 shrink-0 rounded-full bg-muted text-[11px] font-bold flex items-center justify-center" aria-hidden="true">{i + 1}</span>
                <span>
                  <strong className="text-foreground">{t(`tribunal.phases_long.${p}`)}</strong>
                  <span className="text-muted-foreground"> — {t(`tribunal.court.flow.${p}`)}</span>
                  {p === 'verdict' && famous && <span className="text-muted-foreground"> {t('tribunal.court.flow.famous_tail')}</span>}
                </span>
              </li>
            ))}
          </ol>
        </div>

        {famous?.disclaimer && (
          <p className="mt-auto pt-4 text-xs leading-relaxed text-muted-foreground">{famous.disclaimer}</p>
        )}
      </aside>
      </div>

      {/* Téléphone : l'action reste sous le pouce, par-dessus le dossier */}
      {bar && (
        <div className="lg:hidden sticky bottom-0 z-20 border-t border-border bg-card/95 backdrop-blur px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {bar}
        </div>
      )}
    </div>
  );
}
